import type { FieldEvidence, Initiative, Objective, Task } from '@portfolio-pm/shared';
import { ChevronRight } from 'lucide-react';

export function TaskPlanningData({
  task,
  tasks,
  objectives,
  initiatives,
  evidence,
}: {
  task: Task;
  tasks: Task[];
  objectives: Objective[];
  initiatives: Initiative[];
  evidence: FieldEvidence[];
}): JSX.Element {
  const tasksById = new Map(tasks.map((candidate) => [candidate.id, candidate]));
  const objectivesById = new Map(objectives.map((objective) => [objective.id, objective]));
  const initiativesById = new Map(initiatives.map((initiative) => [initiative.id, initiative]));
  const taskEvidence = evidence
    .filter((item) => item.entityType === 'task' && item.entityId === task.id)
    .sort((a, b) => b.recordedAt - a.recordedAt || a.field.localeCompare(b.field));

  const decisionRows: PlanningRow[] = [
    row('Commitment', task.commitmentState),
    row('Actionability', task.actionability),
    row('Expected outcome', task.expectedOutcome),
    row('Available when', task.availabilityTrigger),
    row('Waiting reason', task.waitingReason),
    row('Review at', formatTimestamp(task.reviewAt)),
  ];
  const relationshipRows: PlanningRow[] = [
    row('Objective', linkedLabel(task.objectiveId, objectivesById)),
    row('Initiative', linkedLabel(task.initiativeId, initiativesById)),
    row('Key result ID', task.krId),
    row('Planning contexts', formatIds(task.planningContextIds)),
    row(
      'Dependencies',
      task.dependsOn?.length
        ? task.dependsOn.map((id) => tasksById.get(id)?.title ?? id).join(', ')
        : undefined,
    ),
    row('Parent task', linkedLabel(task.parentTaskId, tasksById)),
  ];
  const stateRows: PlanningRow[] = [
    row('Energy', task.energy),
    row('Daypart', task.daypart),
    row('Owner', task.owner),
    row('Source', task.source),
    row('Postponed', task.postponed === undefined ? undefined : `${task.postponed} times`),
    row('Recurrence series', task.recurrence?.seriesId),
    row('Task ID', task.id, true),
    row('Project ID', task.projectId, true),
    row('Created', formatTimestamp(task.createdAt)),
    row('Updated', formatTimestamp(task.updatedAt)),
    row('Completed', formatTimestamp(task.completedAt)),
    row('Trashed', formatTimestamp(task.trashedAt)),
  ];

  return (
    <details className="group pane-divider-t" data-testid="task-planning-data">
      <summary className="flex min-h-10 cursor-pointer list-none items-center gap-2 px-2 text-sm font-medium text-[color:var(--app-text)] outline-none transition hover:bg-[color:var(--app-surface-muted)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgba(185,108,45,0.3)] [&::-webkit-details-marker]:hidden">
        <ChevronRight
          aria-hidden="true"
          size={15}
          className="shrink-0 text-[color:var(--app-text-soft)] transition-transform group-open:rotate-90"
        />
        <span>Planning data</span>
        <span className="ml-auto text-xs font-normal text-[color:var(--app-text-soft)]">
          read-only
        </span>
      </summary>
      <div className="pane-divider-t bg-[color:var(--app-surface-muted)]/35 px-3 py-3">
        <PlanningGroup title="Decision" rows={decisionRows} />
        <PlanningGroup title="Relationships" rows={relationshipRows} />
        <PlanningGroup title="Internal state" rows={stateRows} />
        <PlanningEvidence evidence={taskEvidence} />
      </div>
    </details>
  );
}

interface PlanningRow {
  label: string;
  value: string | undefined;
  code?: boolean;
}

function row(label: string, value: string | undefined, code = false): PlanningRow {
  return { label, value, code };
}

function PlanningGroup({ title, rows }: { title: string; rows: PlanningRow[] }): JSX.Element {
  return (
    <section className="mb-4 last:mb-0">
      <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--app-text-soft)]">
        {title}
      </h4>
      <dl className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs leading-5">
        {rows.map((item) => (
          <div key={item.label} className="contents">
            <dt className="text-[color:var(--app-text-soft)]">{item.label}</dt>
            <dd
              className={`min-w-0 break-words text-[color:var(--app-text)]${item.code ? ' font-mono text-[11px]' : ''}`}
            >
              {item.value ? humanize(item.value) : <span aria-label="Not set">—</span>}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function PlanningEvidence({ evidence }: { evidence: FieldEvidence[] }): JSX.Element | null {
  if (evidence.length === 0) return null;
  return (
    <section>
      <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--app-text-soft)]">
        Field evidence
      </h4>
      <ul className="space-y-1 text-xs leading-5">
        {evidence.map((item) => (
          <li key={item.id} className="grid grid-cols-[7.25rem_minmax(0,1fr)] gap-x-3">
            <span className="text-[color:var(--app-text-soft)]">{humanize(item.field)}</span>
            <span className="min-w-0 break-words text-[color:var(--app-text)]">
              {humanize(item.valueState)} · {humanize(item.source)} · {item.actor}
              {item.tool ? ` via ${item.tool}` : ''} · {formatTimestamp(item.recordedAt)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function linkedLabel<T extends { id: string; title: string }>(
  id: string | undefined,
  records: Map<string, T>,
): string | undefined {
  if (!id) return undefined;
  const record = records.get(id);
  return record ? `${record.title} (${id})` : id;
}

function formatIds(ids: string[] | undefined): string | undefined {
  return ids?.length ? ids.join(', ') : undefined;
}

function formatTimestamp(value: number | undefined): string | undefined {
  if (value === undefined) return undefined;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ');
}
